// One-time, idempotent migration: users who built a "Corner" during onboarding
// before R4 wrote the canonical `favoriteFighters` shape have their picks
// stranded in legacy favoriteFighterIds/favoriteFighterNames. Profile /
// FavoritesManager / publicProfiles only ever read favoriteFighters, so those
// users show an empty Corner everywhere except the (legacy-aware) empty vault
// stage. This backfills favoriteFighters from the legacy arrays — it NEVER
// overwrites an existing non-empty favoriteFighters, so a Profile pick always
// wins over a stale onboarding pick (safe merge, no wipe).
//
// `legacyFavoritesMigrated` is a durable sentinel so a legacy account whose
// ids all resolve to nothing (all fallback:/malformed) still counts as
// "already migrated" once we persist `favoriteFighters: []` — without it,
// an empty result keeps failing the "already migrated" (non-empty array)
// check and this would re-evaluate (and re-write) on every future load.
import { splitFighterName } from '../features/onboarding/onboardingHelpers';
import { updateUserProfile } from '../firebase/profileService';

/** Pure — returns a merge patch, or null if no migration is needed. */
export function buildLegacyFavoriteFightersMigration(profile) {
  if (!profile) return null;
  if (profile.legacyFavoritesMigrated === true) return null;
  if (Array.isArray(profile.favoriteFighters) && profile.favoriteFighters.length > 0) return null;

  const ids = Array.isArray(profile.favoriteFighterIds) ? profile.favoriteFighterIds : [];
  if (!ids.length) return null;
  const names = Array.isArray(profile.favoriteFighterNames) ? profile.favoriteFighterNames : [];

  const favoriteFighters = ids
    .map((id, i) => {
      if (typeof id !== 'string' || !id || id.startsWith('fallback:')) return null;
      const { firstName, lastName } = splitFighterName(names[i] || '');
      return { id, firstName, lastName, weightClass: '', profileURL: '' };
    })
    .filter(Boolean)
    .slice(0, 5);

  // Always mark migrated once we've evaluated real legacy ids — even a
  // zero-result migration (all ids bogus) must stick, or this re-runs forever.
  return { favoriteFighters, legacyFavoritesMigrated: true };
}

/** Fire-and-forget writer — safe to call on every profile load (no-op once migrated). */
export async function runLegacyFavoritesMigration(uid, profile) {
  const patch = buildLegacyFavoriteFightersMigration(profile);
  if (!patch || !uid) return false;
  await updateUserProfile(uid, patch);
  return true;
}

const favoritesMigrationExport = {
  buildLegacyFavoriteFightersMigration,
  runLegacyFavoritesMigration,
};

export default favoritesMigrationExport;
